import React, { useContext, useEffect, useState } from "react";
import LandLordContext from "../../context/landlord/LandLordContext";
import useTranslator from "../../hooks/useTranslator";
import moment from "moment";
import Loader from "../Loader";
// LoadingSpinner removed — using main Loader instead
import { formatPropertyAddress, getUserDisplayName } from "../../helper/common";
import { transformAwardSnapshot } from "../../Utils/transformAwardSnapshot";
import axiosInstance from "../../services/authWrapper";
import "./PropertyHistoryTimeline.css";

const PropertyHistoryTimeline = ({
  property,
  user,
  currentLang,
  onBack,
  onViewSnapshot,
}) => {
  const { _t } = useTranslator();
  const { getPropertyHistory } = useContext(LandLordContext);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalRecords: 0,
    totalPages: 1,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [snapshotLoadingId, setSnapshotLoadingId] = useState(null);

  const workspaceId =
    user?.current_workspace?._id || user?.current_workspace?.id;

  const fetchHistory = async (activeFilter, pageNum = 1, append = false) => {
    if (!property?._id || !workspaceId) return;

    if (pageNum === 1 && !append) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const res = await getPropertyHistory(property._id, {
        workspace_id: workspaceId,
        filter: activeFilter,
        page: pageNum,
        limit: 20,
      });

      if (res && res.status === "success") {
        const newEvents = res.data || [];
        setEvents((prev) => (append ? [...prev, ...newEvents] : newEvents));
        setPagination(
          res.pagination || {
            page: pageNum,
            limit: 20,
            totalRecords: newEvents.length,
            totalPages: 1,
          }
        );
      }
    } catch (err) {
      console.error("Error fetching property history:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchHistory(filter, 1, false);
  }, [property?._id, workspaceId, filter]);

  const handleFilterChange = (newFilter) => {
    if (newFilter === filter) return;
    setFilter(newFilter);
  };

  const handleLoadMore = () => {
    if (pagination.page < pagination.totalPages && !loadingMore) {
      fetchHistory(filter, pagination.page + 1, true);
    }
  };

  const getActorName = (actor) => {
    if (!actor) return "";
    if (actor.fname_companyName || actor.lname) {
      return [actor.fname_companyName, actor.lname].filter(Boolean).join(" ");
    }
    return getUserDisplayName(actor, "");
  };

  const getEventTitle = (event) => {
    const type = event?.event_type;
    if (type === "tenant_linked") {
      return _t("tenantLinked", "Tenant linked");
    }
    if (type === "tenant_unlinked") {
      return _t("tenantUnlinked", "Tenant unlinked");
    }
    if (type === "property_awarded") {
      return _t("propertyAwarded", "Property awarded");
    }
    if (type === "property_unawarded") {
      return _t("propertyUnawarded", "Property unawarded");
    }
    if (type === "application_linked") {
      return _t("applicationLinked", "Application linked");
    }
    if (type === "application_unlinked") {
      return _t("applicationUnlinked", "Application unlinked");
    }
    if (type === "shared_to") {
      return _t("sharedTo", "Shared to");
    }
    if (type === "origin") {
      return _t("origin", "Origin");
    }
    if (
      type === "copied_from" ||
      type?.includes("imported") ||
      type?.includes("inherited") ||
      event?.inherited
    ) {
      const wsName =
        event?.inherited_from_workspace?.name ||
        event?.actor_workspace?.name ||
        "";
      return wsName
        ? `${_t("importedFrom", "Imported from")} ${wsName}`
        : _t("importedFromWorkspace", "Imported from Workspace");
    }
    if (event?.title) return event.title;
    return type
      ? type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      : _t("activity", "Activity");
  };

  const getEventDetails = (event) => {
    if (event?.details) return event.details;
    if (event?.meta?.details) return event.meta.details;

    const type = event?.event_type;
    if (
      type === "copied_from" ||
      type?.includes("imported") ||
      type?.includes("inherited") ||
      event?.inherited
    ) {
      return _t("tenantsAndHistoryImported", "Tenants and history imported");
    }

    if (
      Array.isArray(event?.meta?.tenant_names) &&
      event.meta.tenant_names.length > 0
    ) {
      return event.meta.tenant_names.join(", ");
    }
    if (
      event?.meta?.tenant_names &&
      typeof event.meta.tenant_names === "string"
    ) {
      return event.meta.tenant_names;
    }

    if (Array.isArray(event?.tenant_names) && event.tenant_names.length > 0) {
      return event.tenant_names.join(", ");
    }

    if (event?.tenant_names && typeof event.tenant_names === "string") {
      return event.tenant_names;
    }

    if (Array.isArray(event?.tenants) && event.tenants.length > 0) {
      return event.tenants
        .map((t) =>
          typeof t === "object"
            ? [t?.fname, t?.lname].filter(Boolean).join(" ") ||
              t?.forensic_name ||
              t?.email
            : t
        )
        .filter(Boolean)
        .join(", ");
    }

    if (event?.tenant_id) {
      const t = event.tenant_id;
      if (typeof t === "object") {
        return (
          [t?.fname, t?.lname].filter(Boolean).join(" ") ||
          t?.forensic_name ||
          t?.email ||
          ""
        );
      }
      return String(t);
    }

    return "";
  };

  const handleSnapshotClick = async (event) => {
    if (event?.event_type === "property_awarded") {
      setSnapshotLoadingId(event._id);
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/users/history-award-snapshot/${event._id}`
        );
        const snapshotData = res.data?.data || res.data;
        const profiles = transformAwardSnapshot(snapshotData);
        if (onViewSnapshot) onViewSnapshot(profiles, event._id);
      } catch (err) {
        console.error("Failed to load snapshot:", err);
      } finally {
        setSnapshotLoadingId(null);
      }
    } else if (onViewSnapshot) {
      onViewSnapshot(event);
    }
  };

  const renderNodeIcon = (event) => {
    const type = event?.event_type;

    if (type === "tenant_linked" || type === "application_linked") {
      return (
        <div className="history-node-icon icon-green">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ffffff"
            strokeWidth="3.5"
            strokeLinecap="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </div>
      );
    }

    if (type === "property_awarded") {
      return (
        <div className="history-node-icon icon-green">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ffffff"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      );
    }

    if (type === "shared_to") {
      return (
        <div className="history-node-icon icon-outline">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4B5563"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 17 17 7" />
            <path d="M8 7h9v9" />
          </svg>
        </div>
      );
    }

    if (
      type === "copied_from" ||
      type?.includes("imported") ||
      type?.includes("inherited") ||
      event?.inherited
    ) {
      return (
        <div className="history-node-icon icon-outline">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4B5563"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </div>
      );
    }

    if (
      type === "tenant_unlinked" ||
      type === "property_unawarded" ||
      type === "application_unlinked"
    ) {
      return (
        <div className="history-node-icon icon-gray">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ffffff"
            strokeWidth="3.5"
            strokeLinecap="round"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </div>
      );
    }

    return (
      <div className="history-node-icon icon-gray">
        <span className="dot" />
      </div>
    );
  };

  const propertyRent = property?.report_rent ?? property?.rent ?? 0;
  const propertyAddress = formatPropertyAddress(property, currentLang);
  const propertySubLocation = [
    property?.municipality || property?.city,
    property?.province,
    property?.postcode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="property-history-container">
      {/* Property Summary Header Card */}
      <div className="rental-property-summary">
        <div className="rental-property-info">
          <div className="rental-property-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10.7068 0.29279L19.7051 9.29291C20.335 9.92291 19.889 10.9999 18.9982 10.9999H17.9984V16.9999C17.9984 17.7956 17.6824 18.5587 17.1199 19.1213C16.5574 19.6839 15.7945 20 14.9989 20H13.9992V12.9999C13.9992 12.2347 13.7069 11.4984 13.182 10.9416C12.6572 10.3849 11.9394 10.0498 11.1756 10.0049L10.9997 9.99988H9.00005C8.20458 9.99988 7.44164 10.3159 6.87917 10.8786C6.31664 11.4412 6.00064 12.2042 6.00064 12.9999V20H5.00082C4.20531 20 3.4424 19.6839 2.87989 19.1213C2.31739 18.5587 2.00137 17.7956 2.00137 16.9999V10.9999H1.00156C0.111727 10.9999 -0.33519 9.92291 0.294693 9.29291L9.29299 0.29279C9.48052 0.105316 9.73476 0 9.99987 0C10.265 0 10.5193 0.105316 10.7068 0.29279ZM10.9997 11.9999C11.2649 11.9999 11.5192 12.1053 11.7067 12.2928C11.8942 12.4804 11.9995 12.7347 11.9995 12.9999V20H8.00029V12.9999C8.00029 12.755 8.09017 12.5186 8.25293 12.3356C8.41564 12.1525 8.63988 12.0356 8.88311 12.0069L9.00005 11.9999H10.9997Z"
                fill="#253837"
              />
            </svg>
          </div>

          <div className="rental-property-text">
            <h3>{propertyAddress}</h3>
            <p>
              {[property?.province, property?.postcode]
                .filter(Boolean)
                .join(", ")}
            </p>
          </div>
        </div>

        <div className="rental-rent-card">
          <span className="rental-rent-label">{_t("propertyRent")}</span>

          <div className="rental-rent-input">
            <span className="rental-currency">€</span>
            <input
              placeholder="0"
              type="number"
              value={propertyRent}
              disabled={true}
            />
            <span className="rental-period">/mo</span>
          </div>
        </div>
      </div>

      {/* Activity Filter Header */}
      <div className="history-activity-section">
        <h6 className="activity-title">{_t("activity", "ACTIVITY")}</h6>
        <div className="activity-filter-pills">
          <button
            className={`filter-pill ${filter === "all" ? "active" : ""}`}
            onClick={() => handleFilterChange("all")}
          >
            {_t("all", "All")}
          </button>
          <button
            className={`filter-pill ${filter === "tenants" ? "active" : ""}`}
            onClick={() => handleFilterChange("tenants")}
          >
            {_t("tenants", "Tenants")}
          </button>
          <button
            className={`filter-pill ${filter === "awards" ? "active" : ""}`}
            onClick={() => handleFilterChange("awards")}
          >
            {_t("awards", "Awards")}
          </button>
        </div>
      </div>

      {/* Timeline Section */}
      {loading ? (
        <div className="py-5">
          <Loader />
        </div>
      ) : events.length === 0 ? (
        <div className="py-5 text-center text-muted">
          <p>{_t("noPropertyHistoryFound", "No property history found.")}</p>
        </div>
      ) : (
        <div className="history-timeline">
          <div className="timeline-line" />
          {events.map((event, index) => {
            const dateStr = moment(event.created_at || event.createdAt).format(
              "DD MMM YYYY · HH:mm"
            );
            const title = getEventTitle(event);
            const details = getEventDetails(event);
            const actorName = getActorName(event.actor);
            const workspaceName =
              event.actor_workspace?.name ||
              event.inherited_from_workspace?.name;
            const isAwardEvent = event.event_type === "property_awarded";

            return (
              <div key={event._id || index} className="timeline-item">
                {renderNodeIcon(event)}

                <div className="timeline-content">
                  <div className="timeline-date">{dateStr}</div>
                  <h5 className="timeline-event-title">{title}</h5>

                  {details && <p className="timeline-details">{details}</p>}

                  <div className="timeline-meta d-flex align-items-center gap-2">
                    {actorName && (
                      <span className="timeline-actor">
                        {_t("by", "by")} {actorName}
                      </span>
                    )}
                    {workspaceName && (
                      <span className="timeline-workspace-badge">
                        {workspaceName}
                      </span>
                    )}
                  </div>

                  {isAwardEvent && (
                    <div className="mt-2">
                      <button
                        className="btn-view-snapshot"
                        onClick={() => handleSnapshotClick(event)}
                        disabled={snapshotLoadingId === event._id}
                      >
                        {snapshotLoadingId === event._id ? (
                          <>{_t("loading")}...</>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                              <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                              <path d="M10 9H8" />
                              <path d="M16 13H8" />
                              <path d="M16 17H8" />
                            </svg>
                            {_t("viewReportSnapshot", "View report snapshot")}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {pagination.page < pagination.totalPages && (
            <div className="text-center mt-3 mb-2">
              <button
                className="btn btn-sm btn-outline-secondary px-3 py-1 rounded-pill"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? <Loader /> : _t("loadMore", "Load more")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyHistoryTimeline;
